import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogShell } from '../ui/DialogShell';
import { useAuth } from '../../context/AuthContext';
import { useRegularizacion } from '../../context/RegularizacionContext';
import { useExigenciaComprobantes } from '../../hooks/useExigenciaComprobantes';
import { rutaPanelPendientes } from '../../utils/comprobanteEntidad';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { claveInicioSesion, ventanaRegularizacionAbierta } from '../../utils/ventanaRegularizacion';

const MAXIMO_LISTADO = 5;

/** Una vez por inicio de sesión: se recuerda con la fecha del último login de Supabase, por usuario. */
export const VentanaRegularizacion: React.FC = () => {
  const { usuario, authUser } = useAuth();
  const { entidad, vencidos } = useRegularizacion();
  const exigenciaVigente = useExigenciaComprobantes();
  const navigate = useNavigate();
  // Qué inicio de sesión cerró la ventana: otra identidad o un login nuevo no hereda el cierre.
  const [cerradaEnSesion, setCerradaEnSesion] = useState<string | null>(null);

  const inicioSesion = authUser?.last_sign_in_at ?? '';
  const sesion = claveInicioSesion(usuario?.user_id ?? null, inicioSesion);
  const clave = usuario ? `comprobantes-regularizacion-vista:${usuario.user_id}` : null;
  const abierta = ventanaRegularizacionAbierta({
    sesion,
    cerradaEnSesion,
    yaVistaEnEstaSesion: clave !== null && localStorage.getItem(clave) === inicioSesion,
    hayEntidad: entidad !== null,
    cantidadVencidos: vencidos.length,
  });

  const marcarVista = () => {
    if (clave) localStorage.setItem(clave, inicioSesion);
    setCerradaEnSesion(sesion);
  };

  if (!abierta || !entidad) return null;

  const restantes = vencidos.length - MAXIMO_LISTADO;

  return (
    <DialogShell
      isOpen
      onClose={marcarVista}
      size="sm"
      title={`Hay ${vencidos.length} ${vencidos.length === 1 ? 'comprobante' : 'comprobantes'} con más de 7 días sin adjuntar`}
    >
      <div className="space-y-4">
        <ul className="text-sm text-slate-700 space-y-1">
          {vencidos.slice(0, MAXIMO_LISTADO).map((f) => (
            <li key={`${f.movimiento_id}:${f.respaldo}`}>
              {formatDate(f.fecha)} · {f.descripcion ?? 'Sin descripción'} · {formatCurrency(Number(f.monto))}
            </li>
          ))}
        </ul>
        {restantes > 0 && <p className="text-sm text-slate-500">y {restantes} más</p>}
        {exigenciaVigente === true && (
          <p className="text-sm text-slate-600">
            Mientras falten, cada movimiento nuevo debe registrarse con su comprobante.
          </p>
        )}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={marcarVista}
            className="flex-1 min-h-11 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={() => { marcarVista(); navigate(rutaPanelPendientes(entidad)); }}
            className="flex-1 min-h-11 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700"
          >
            Ver pendientes
          </button>
        </div>
      </div>
    </DialogShell>
  );
};
